import { useEffect, useState } from "react";

import KundeNeuDialog from "../kunden/KundeNeuDialog";
import StrassenFeld from "../kunden/StrassenFeld";
import { KundenCombo } from "../planer/Combos";
import { useCreateProjekt, useKunden, useProjektStatusOptions, useTeamOwners } from "../../hooks/usePlanung";
import { parseBetrag } from "../../lib/belege";
import { composeKundeAdresse, pflegeTitel } from "../../lib/projekte";
import { errorText } from "../../lib/invoices";
import type { Kunde, ProjektCreateBody, ProjektDetail } from "../../lib/types";
import "../../styles/post.css";

/**
 * Neues Projekt anlegen, ohne den Termin zu verlassen.
 *
 * Bis hierher musste man den Termin abbrechen, zur Projektseite, dort anlegen
 * und von vorn beginnen (Benjamin, 11.09.2026). Die Maske fragt dieselben
 * Felder ab wie das Panel der Projektseite.
 *
 * Zwei bewusste Unterschiede zu dort:
 *
 * 1. Sie **navigiert nicht** in das neue Projekt, sondern gibt es zurück —
 *    der Aufrufer stellt es ein und der Nutzer schreibt am Termin weiter.
 * 2. Der Kunde wird über dieselbe `KundenCombo` gewählt wie im Termin, und die
 *    kann selbst anlegen. Ein Projekt ohne Kunden weist der Server ab
 *    (`planer.projekt_create`), also muss die Kette Termin → Projekt → Kunde
 *    an einem Stück gehen.
 *
 * Seit 13.10.2026 ist das die einzige Maske für neue Projekte — auch auf der
 * Projektseite, deren eigenes Panel Kunden mit einer Freitext-Adresse anlegte.
 * Die Baustelle kommt vorbelegt mit der vollen Kundenanschrift (Straße, PLZ Ort —
 * daran hängen Fotos nach Aufnahmeort und Termin-Ort) und lässt sich über die
 * Adresssuche ändern.
 *
 * Die Klassen sind die des Kundendialogs — ein zweites Aussehen für dieselbe
 * Sache wäre keine zweite Maske, sondern ein zweites Produkt. Sie stehen in
 * `invoice-editor.css`; Kontor liefert ein einziges CSS-Bündel aus, deshalb
 * gelten sie auch im Planer.
 */

/* Der Server kennt nur diese beiden; eine Konstante gab es bisher nirgends,
   die Projektseite hat sie inline stehen. */
const PROJEKTARTEN: Array<{ wert: string; label: string }> = [
  { wert: "bau", label: "Bauprojekt" },
  { wert: "pflege", label: "Pflegeprojekt" },
];

type Props = {
  /** Was im Suchfeld stand — wird als Projektname vorgeschlagen. */
  vorschlagTitel?: string;
  /** Kunde, der schon feststeht (z. B. aus dem Angebot heraus). */
  kundeId?: string;
  /** Kleiner Satz neben dem Titel: wohin das neue Projekt gleich wandert. */
  hinweis?: string;
  onAngelegt: (projekt: ProjektDetail) => void;
  onSchliessen: () => void;
};

export default function ProjektNeuDialog({
  vorschlagTitel = "", kundeId: kundeVorgabe = "", hinweis = "landet in der Projektliste und wird gleich in den Termin gesetzt",
  onAngelegt, onSchliessen,
}: Props) {
  const anlegen = useCreateProjekt();
  const { data: kunden = [] } = useKunden();
  const { data: owners = [] } = useTeamOwners();
  const { data: statusOpts = [] } = useProjektStatusOptions();

  const [title, setTitle] = useState(vorschlagTitel.trim());
  /** Solange niemand den Namen angefasst hat, vergibt ihn bei Pflege das Muster. */
  const [titelVonHand, setTitelVonHand] = useState(!!vorschlagTitel.trim());
  const [kundeId, setKundeId] = useState(kundeVorgabe);
  const [status, setStatus] = useState("🟡 anfrage");
  const [projektart, setProjektart] = useState("bau");
  const [ort, setOrt] = useState("");
  const [start, setStart] = useState("");
  const [nettowert, setNettowert] = useState("");
  const [verantwortlicher, setVerantwortlicher] = useState("");
  const [notes, setNotes] = useState("");
  const [fehler, setFehler] = useState("");

  /* Frisch angelegte Kunden stehen noch nicht in der Liste des Servers. Sie
     hier vorzuhängen ist kein Zwischenspeicher, sondern der Unterschied
     zwischen „ausgewählt" und „ein leeres Feld, bis der Refetch kommt". */
  const [kundeNeu, setKundeNeu] = useState<{ vorschlag: string } | null>(null);
  const [frischerKunde, setFrischerKunde] = useState<Kunde | null>(null);
  const kundenListe = frischerKunde && !kunden.some((k) => k.id === frischerKunde.id)
    ? [frischerKunde, ...kunden]
    : kunden;

  /* Steht der Kunde schon fest, kommt der Ort von ihm — sobald die Liste da ist. */
  useEffect(() => {
    if (!kundeVorgabe || ort) return;
    const k = kunden.find((x) => x.id === kundeVorgabe);
    if (k) setOrt(composeKundeAdresse(k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeVorgabe, kunden]);

  useEffect(() => {
    if (projektart !== "pflege" || titelVonHand) return;
    const k = kundenListe.find((x) => x.id === kundeId);
    setTitle(k ? pflegeTitel(k.name, start) : "");
  }, [projektart, kundeId, start, titelVonHand, kundenListe]);

  /* Pflege beginnt aktiv, Bau als Anfrage — wie im Planer. */
  const artWaehlen = (art: string) => {
    setProjektart(art);
    setStatus(art === "pflege" ? "🟢 aktiv" : "🟡 anfrage");
    if (art === "bau" && !titelVonHand) setTitle("");
  };

  useEffect(() => {
    const aufTaste = (event: KeyboardEvent) => {
      /* Nur schließen, wenn kein Kundendialog darüber liegt — sonst nähme ein
         Escape beide mit, und die halb ausgefüllte Projektmaske wäre weg. */
      if (event.key === "Escape" && !kundeNeu) onSchliessen();
    };
    document.addEventListener("keydown", aufTaste);
    return () => document.removeEventListener("keydown", aufTaste);
  }, [onSchliessen, kundeNeu]);

  /* Der Ort steht meist schon beim Kunden. Nur füllen, solange niemand etwas
     eigenes eingetragen hat — die Baustelle liegt nicht immer an der
     Rechnungsadresse. */
  const kundeWaehlen = (id: string) => {
    setKundeId(id);
    if (ort.trim()) return;
    const k = kundenListe.find((x) => x.id === id);
    if (k) setOrt(composeKundeAdresse(k));
  };

  const speichern = (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) { setFehler("Der Projektname fehlt."); return; }
    if (!kundeId) { setFehler("Ein Projekt braucht einen Kunden — auswählen oder neu anlegen."); return; }
    setFehler("");
    const netto = parseBetrag(nettowert);
    const body: ProjektCreateBody = {
      title: title.trim(),
      kunde_id: kundeId,
      ort: ort.trim(),
      status,
      projektart,
      start,
      notes,
      verantwortlicher,
      ...(netto != null ? { nettowert: netto } : {}),
    };
    anlegen.mutate(body, {
      onSuccess: (projekt) => onAngelegt(projekt),
      onError: (error) => setFehler(errorText(error)),
    });
  };

  return (
    <>
      <div
        className="invoice-pflege-backdrop"
        onMouseDown={(event) => { if (event.target === event.currentTarget) onSchliessen(); }}
      >
        <form
          className="invoice-pflege-dialog kunde-neu-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Neues Projekt anlegen"
          onSubmit={speichern}
        >
          <div className="invoice-titel-pflege-kopf">
            <span>Neues Projekt</span>
            <small>{hinweis}</small>
          </div>

          {/* Zuerst die Art: sie entscheidet über Name und Anfangsstatus. */}
          <div className="vg-art-wahl" role="radiogroup" aria-label="Projektart">
            {PROJEKTARTEN.map((a) => (
              <button
                key={a.wert} type="button" role="radio" aria-checked={projektart === a.wert}
                className={"vg-art-knopf" + (projektart === a.wert ? " is-active" : "")}
                onClick={() => artWaehlen(a.wert)}
              >
                <strong>{a.label}</strong>
                <small>{a.wert === "pflege" ? "Name kommt von selbst · startet aktiv" : "eigener Name · startet als Anfrage"}</small>
              </button>
            ))}
          </div>

          <div className="kunde-neu-felder">
            <label className="ist-breit">
              <span>Projektname *</span>
              <input value={title} onChange={(e) => { setTitle(e.target.value); setTitelVonHand(true); }}
                placeholder={projektart === "pflege" ? "wird aus Kunde und Monat gebildet" : "z.B. Gartenbau Müller"} autoFocus={projektart !== "pflege"} />
            </label>
          </div>

          <div className="kunde-neu-abschnitt">
            <div className="kunde-neu-abschnitt-kopf"><span>Kunde *</span></div>
            {/* Dieselbe Combo wie im Termin, mit derselben Fußzeile zum
                Anlegen — der Nutzer lernt das Feld nur einmal. */}
            <KundenCombo
              kunden={kundenListe}
              value={kundeId}
              onChange={kundeWaehlen}
              onNeu={(vorschlag) => setKundeNeu({ vorschlag })}
            />
          </div>

          <div className="kunde-neu-abschnitt">
            <div className="kunde-neu-abschnitt-kopf"><span>Einordnung</span></div>
            <div className="kunde-neu-felder">
              <label>
                <span>Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {/* Die Werte kommen vom Server (crm/config.yaml); ohne
                      Antwort bleibt wenigstens die Vorbelegung wählbar. */}
                  {(statusOpts.length ? statusOpts : [status]).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="ist-breit">
                <span>Baustelle</span>
                {/* Vorbelegt mit der Kundenanschrift; ein Vorschlag setzt „Straße, PLZ Ort". */}
                <StrassenFeld
                  value={ort}
                  onChange={setOrt}
                  onPick={(t) => setOrt(composeKundeAdresse({ strasse: t.strasse, plz: t.plz, ort: t.ort }))}
                  placeholder="Adresse der Baustelle — wie Kunde, wenn leer"
                />
              </label>
              <label>
                <span>Start</span>
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              </label>
              <label>
                <span>Verantwortlicher</span>
                <select value={verantwortlicher} onChange={(e) => setVerantwortlicher(e.target.value)}>
                  <option value="">— wählen —</option>
                  {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              </label>
              <label>
                <span>Nettowert (optional)</span>
                {/* inputMode decimal: am Telefon der Ziffernblock mit Komma.
                    `parseBetrag` versteht „1.200,00" — der Inline-Code auf der
                    Projektseite machte daraus still NaN. */}
                <input value={nettowert} onChange={(e) => setNettowert(e.target.value)} inputMode="decimal" placeholder="0,00" />
              </label>
            </div>
          </div>

          <div className="kunde-neu-felder" style={{ marginTop: 12 }}>
            <label className="ist-breit">
              <span>Notizen</span>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
          </div>

          {fehler && <p className="invoice-titel-pflege-fehler" role="alert">{fehler}</p>}
          <div className="invoice-titel-pflege-aktionen">
            <button type="button" className="invoice-button" onClick={onSchliessen}>Abbrechen</button>
            <button type="submit" className="invoice-button primary" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Legt an …" : "Projekt anlegen und übernehmen"}
            </button>
          </div>
        </form>
      </div>

      {/* Geschwister statt Kind: läge er im Backdrop, schlösse sein
          Hintergrundklick beide Masken auf einmal. */}
      {kundeNeu && (
        <KundeNeuDialog
          vorschlagName={kundeNeu.vorschlag}
          hinweis="landet im Kundenstamm und wird gleich in das Projekt gesetzt"
          onAngelegt={(kunde) => {
            setFrischerKunde(kunde);
            setKundeId(kunde.id);
            if (!ort.trim()) setOrt(composeKundeAdresse(kunde));
            setKundeNeu(null);
          }}
          onSchliessen={() => setKundeNeu(null)}
        />
      )}
    </>
  );
}
